import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Space, Popconfirm, message, Card, Typography } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { blogsApi } from '../../services/api';

const { Title } = Typography;

const BlogsList = () => {
  const [blogsList, setBlogsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const navigate = useNavigate();

  const fetchBlogsList = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const response = await blogsApi.getList({ page });
      console.log('API Response:', response.data); // 调试信息
      const processedRows = (response.data.rows || []).map(item => ({
        ...item,
        title: item.title ? decodeURIComponent(item.title) : 'タイトルなし',
        content: item.content ? decodeURIComponent(item.content) : '内容なし'
      }));
      setBlogsList(processedRows);
      // 使用函数式更新避免闭包问题
      setPagination(prev => ({
        ...prev,
        current: page,
        total: response.data.count || 0,
      }));
    } catch (error) {
      console.error('获取ブログ列表失败:', error); // 调试信息
      message.error('ブログ一覧の取得に失敗しました：' + (error.response?.data?.message || error.message || 'ネットワークエラー'));
    } finally {
      setLoading(false);
    }
  }, []); // 移除pagination依赖，避免无限循环

  useEffect(() => {
    fetchBlogsList(1);
  }, [fetchBlogsList]);
  
  // 添加分页参数变化监听
  useEffect(() => {
    if (pagination.current !== 1) {
      fetchBlogsList(pagination.current);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, fetchBlogsList]);

  const handleTableChange = (newPagination) => {
    setPagination(prev => ({
      ...prev,
      current: newPagination.current
    }));
    // 不再直接调用fetchBlogsList，由useEffect触发
  };

  const handleDelete = async (id) => {
    try {
      await blogsApi.delete(id);
      message.success('削除しました');
      fetchBlogsList(pagination.current);
    } catch (error) {
      message.error('削除に失敗しました：' + (error.response?.data?.message || 'ネットワークエラー'));
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'タイトル',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
    },
    {
      title: 'ソート',
      dataIndex: 'sort',
      key: 'sort',
      width: 100,
    },
    {
      title: '作成日時',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 200,
      render: (text) => new Date(text).toLocaleString('ja-JP'),
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={() => navigate(`/blogs/edit/${record.id}`)}
          >
            編集
          </Button>
          <Popconfirm
            title="削除確認"
            description="このブログを削除してもよろしいですか？"
            onConfirm={() => handleDelete(record.id)}
            okText="はい"
            cancelText="いいえ"
          >
            <Button
              type="primary"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              削除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={4} style={{ margin: 0 }}>ブログ一覧</Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/blogs/create')}
        >
          ブログ作成
        </Button>
      </div>
      <Table
        columns={columns}
        dataSource={blogsList}
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: false,
          showQuickJumper: true,
        }}
        onChange={handleTableChange}
        rowKey="id"
      />
    </Card>
  );
};

export default BlogsList;
